<template>
  <header class="rm-navbar">
    <!-- 全量菜单按钮 -->
    <button class="rm-navbar__menu-toggle" title="全量菜单" @click="showSidebarDrawer = true">
      <i class="el-icon-s-grid"></i>
    </button>

    <!-- Logo区域 -->
    <div class="rm-navbar__logo" @click="$router.push('/')">
      <div class="rm-navbar__logo-icon">
        <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M12 2L2 7L12 12L22 7L12 2Z" fill="currentColor" opacity="0.8"/>
          <path d="M2 17L12 22L22 17" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
          <path d="M2 12L12 17L22 12" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
      </div>
      <span class="rm-navbar__logo-text">报告管理</span>
    </div>

    <!-- 顶部菜单 -->
    <nav class="rm-navbar__menu">
      <template v-for="item in visibleMenuItems">
        <el-dropdown
          v-if="item.children && item.children.length"
          :key="item.authId"
          trigger="hover"
          placement="bottom-start"
          @command="handleMenuClick"
        >
          <div class="rm-navbar__menu-item" :class="{ 'is-active': isMenuActive(item) }">
            <i :class="item.authIcon || 'el-icon-folder'"></i>
            <span>{{ item.authName }}</span>
            <i class="el-icon-arrow-down rm-navbar__menu-arrow"></i>
          </div>
          <el-dropdown-menu slot="dropdown" class="rm-navbar__submenu">
            <el-dropdown-item
              v-for="child in item.children"
              :key="child.authId"
              :command="menuPath(child.authUrl)"
              :class="{ 'is-active': $route.path === menuPath(child.authUrl) }"
            >
              <i :class="child.authIcon || 'el-icon-document'"></i>
              <span>{{ child.authName }}</span>
            </el-dropdown-item>
          </el-dropdown-menu>
        </el-dropdown>
        <div
          v-else
          :key="item.authId"
          class="rm-navbar__menu-item"
          :class="{ 'is-active': $route.path === menuPath(item.authUrl) }"
          @click="handleMenuClick(item.authUrl)"
        >
          <i :class="item.authIcon || 'el-icon-document'"></i>
          <span>{{ item.authName }}</span>
        </div>
      </template>

      <!-- 更多菜单 -->
      <el-dropdown
        v-if="hiddenMenuItems.length > 0"
        trigger="hover"
        placement="bottom-start"
        @command="handleMenuClick"
      >
        <div class="rm-navbar__menu-item rm-navbar__menu-more">
          <i class="el-icon-menu"></i>
          <span>更多</span>
          <i class="el-icon-arrow-down rm-navbar__menu-arrow"></i>
        </div>
        <el-dropdown-menu slot="dropdown" class="rm-navbar__submenu rm-navbar__submenu--more">
          <template v-for="item in hiddenMenuItems">
            <el-dropdown-item
              v-if="item.children && item.children.length"
              :key="item.authId"
              disabled
              class="rm-navbar__submenu-group"
            >
              <i :class="item.authIcon || 'el-icon-folder'"></i>
              <span>{{ item.authName }}</span>
            </el-dropdown-item>
            <template v-if="item.children && item.children.length">
              <el-dropdown-item
                v-for="child in item.children"
                :key="child.authId"
                :command="menuPath(child.authUrl)"
                :class="{ 'is-active': $route.path === menuPath(child.authUrl) }"
              >
                <i :class="child.authIcon || 'el-icon-document'"></i>
                <span>{{ child.authName }}</span>
              </el-dropdown-item>
            </template>
            <el-dropdown-item
              v-else
              :key="item.authId"
              :command="menuPath(item.authUrl)"
              :class="{ 'is-active': $route.path === menuPath(item.authUrl) }"
            >
              <i :class="item.authIcon || 'el-icon-document'"></i>
              <span>{{ item.authName }}</span>
            </el-dropdown-item>
          </template>
        </el-dropdown-menu>
      </el-dropdown>
    </nav>

    <!-- 右侧工具区 -->
    <div class="rm-navbar__right">
      <!-- 菜单搜索：搜索当前账号权限下所有菜单，点击快速跳转 -->
      <el-popover
        v-model="searchVisible"
        placement="bottom-end"
        width="380"
        trigger="manual"
        popper-class="rm-navbar-search-popper"
        :visible-arrow="false"
        :append-to-body="true"
      >
        <!-- 搜索触发图标 -->
        <button
          slot="reference"
          class="rm-navbar__tool"
          :class="{ 'is-on': searchVisible }"
          title="搜索菜单（Ctrl/Cmd+K）"
          @click="toggleSearch"
        >
          <i class="el-icon-searchplus"></i>
        </button>

        <!-- 搜索弹出面板（所有 class 带 rm- 命名空间前缀，避免与全局样式冲突） -->
        <div class="rm-sp">
          <div class="rm-sp__head">
            <i class="el-icon-search rm-sp__head-icon"></i>
            <el-input
              ref="searchInput"
              v-model="searchKeyword"
              class="rm-sp__head-input"
              placeholder="搜索菜单名称，支持关键词…"
              @keydown.up.native.prevent="moveSearchActive(-1)"
              @keydown.down.native.prevent="moveSearchActive(1)"
              @keydown.enter.native.prevent="enterSearch"
              @keydown.esc.native.prevent="closeSearch"
            />
            <kbd class="rm-sp__head-esc">Esc</kbd>
          </div>
          <div class="rm-sp__body">
            <template v-if="searchResults.length">
              <div
                v-for="(item, idx) in searchResults"
                :key="(item.authId || item.authUrl) + '-' + idx"
                class="rm-sp__row"
                :class="{ 'rm-sp__row--on': idx === searchActiveIndex }"
                @mouseenter="searchActiveIndex = idx"
                @click="handleSearchSelect(item)"
              >
                <span class="rm-sp__icon" :class="resolveSearchIconTone(item)">
                  <i :class="item.authIcon || 'el-icon-document'"></i>
                </span>
                <span class="rm-sp__text">
                  <span class="rm-sp__name" v-html="highlightText(item.authName)"></span>
                  <span class="rm-sp__path">{{ item._groupName || item.parentName || '平台功能' }}</span>
                </span>
                <i v-if="idx === 0" class="el-icon-top rm-sp__enter">↵</i>
              </div>
            </template>
            <div v-else class="rm-sp__empty">
              <i class="el-icon-search"></i>
              <p>{{ searchKeyword ? '没有找到匹配的菜单' : '输入关键词搜索全部菜单' }}</p>
            </div>
          </div>
          <div class="rm-sp__foot">
            <span><kbd>↑</kbd><kbd>↓</kbd> 导航</span>
            <span><kbd>↵</kbd> 跳转</span>
            <span><kbd>Esc</kbd> 关闭</span>
          </div>
        </div>
      </el-popover>

      <!-- 消息通知 / 意见反馈：仅普通用户视图显示（管理员视图隐藏） -->
      <button v-if="!isAdminView" class="rm-navbar__tool" title="消息通知" @click="$router.push('/system/notice')">
        <el-badge :value="unreadCount" :hidden="!unreadCount">
          <i class="el-icon-bellplus"></i>
        </el-badge>
      </button>
      <button v-if="!isAdminView" class="rm-navbar__tool" title="意见反馈" @click="$router.push('/feedback/feedbackPage')">
        <i class="el-icon-edit-outlineplus"></i>
      </button>

      <!-- 用户信息 -->
      <el-dropdown trigger="click" class="rm-navbar__user-dropdown" @command="handleAccountCommand">
        <div class="rm-navbar__user">
          <div class="rm-navbar__avatar">
            <img v-if="userInfo.auHeader" :src="userInfo.auHeader" class="rm-navbar__avatar-img" />
            <span v-else>{{ avatarText }}</span>
          </div>
          <span class="rm-navbar__username">{{ userName }}</span>
          <i class="el-icon-arrow-down"></i>
        </div>
        <el-dropdown-menu slot="dropdown">
          <el-dropdown-item command="profile" icon="el-icon-user-solidplus">个人资料</el-dropdown-item>
          <el-dropdown-item command="password" icon="el-icon-lockplus">修改密码</el-dropdown-item>
          <el-dropdown-item divided command="logout" icon="el-icon-switch-buttonplus">退出登录</el-dropdown-item>
        </el-dropdown-menu>
      </el-dropdown>
    </div>

    <!-- 修改密码弹框 -->
    <el-dialog title="修改密码" :visible.sync="passwordDialogVisible" width="45%" :close-on-click-modal="false" append-to-body custom-class="rm-password-dialog">
      <el-form ref="passwordForm" :model="passwordForm" :rules="passwordRules" label-width="80px" size="small">
        <el-form-item label="原密码" prop="auPwOld">
          <el-input v-model="passwordForm.auPwOld" type="password" show-password placeholder="请输入原密码" />
        </el-form-item>
        <el-form-item label="新密码" prop="auPw">
          <el-input v-model="passwordForm.auPw" type="password" show-password placeholder="请输入新密码" />
        </el-form-item>
        <el-form-item label="确认密码" prop="auPwConfirm">
          <el-input v-model="passwordForm.auPwConfirm" type="password" show-password placeholder="请再次输入新密码" />
        </el-form-item>
      </el-form>
      <template #footer>
        <el-button size="small" @click="passwordDialogVisible = false">取 消</el-button>
        <el-button type="primary" size="small" :loading="passwordLoading" @click="handlePasswordSubmit">确 定</el-button>
      </template>
    </el-dialog>

    <!-- 个人资料弹框 -->
    <el-dialog title="个人资料" :visible.sync="profileDialogVisible" width="420px" :close-on-click-modal="false" append-to-body custom-class="rm-profile-dialog">
      <div class="rm-profile">
        <div class="rm-profile__avatar-section">
          <div class="rm-profile__avatar" @click="triggerAvatarUpload">
            <img v-if="userInfo.auHeader" :src="userInfo.auHeader" class="rm-profile__avatar-img" />
            <span v-else class="rm-profile__avatar-text">{{ avatarText }}</span>
            <div class="rm-profile__avatar-overlay"><i class="el-icon-camera"></i></div>
          </div>
          <input ref="avatarInput" type="file" accept="image/*" style="display:none" @change="handleAvatarChange" />
          <p class="rm-profile__avatar-hint" v-if="!avatarUploading">点击更换头像</p>
          <p class="rm-profile__avatar-hint" v-else>上传中...</p>
        </div>
        <div class="rm-profile__info">
          <div class="rm-profile__item">
            <label>姓名</label><span>{{ userInfo.auName || '-' }}</span>
          </div>
          <div class="rm-profile__item">
            <label>账号</label><span>{{ userInfo.auAccount || '-' }}</span>
          </div>
          <div class="rm-profile__item">
            <label>岗位</label><span>{{ userInfo.auPostName || '-' }}</span>
          </div>
          <div class="rm-profile__item">
            <label>邮箱</label><span>{{ userInfo.auEmail || '-' }}</span>
          </div>
        </div>
        <el-button class="rm-profile__pwd-btn" icon="el-icon-edit" size="small" @click="openPasswordFromProfile">修改密码</el-button>
      </div>
    </el-dialog>

    <!-- 左侧抽屉菜单 -->
    <el-drawer
      :visible.sync="showSidebarDrawer"
      direction="ltr"
      size="320px"
      :with-header="false"
      :modal="true"
      :modal-append-to-body="true"
      :append-to-body="true"
      :wrapper-closable="true"
      custom-class="rm-sidebar-drawer"
    >
      <div class="rm-drawer">
        <!-- 顶部品牌区 -->
        <div class="rm-drawer__brand">
          <div class="rm-drawer__brand-bg"></div>
          <div class="rm-drawer__brand-content">
            <div class="rm-drawer__logo">
              <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M12 2L2 7L12 12L22 7L12 2Z" fill="currentColor" opacity="0.9"/>
                <path d="M2 17L12 22L22 17" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                <path d="M2 12L12 17L22 12" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
            </div>
            <div class="rm-drawer__brand-text">
              <span class="rm-drawer__brand-title">报告管理中心</span>
              <span class="rm-drawer__brand-subtitle">Report Management</span>
            </div>
          </div>
          <button class="rm-drawer__close" @click="showSidebarDrawer = false">
            <i class="el-icon-close"></i>
          </button>
        </div>

        <!-- 完整菜单区 -->
        <div class="rm-drawer__menu-wrapper">
          <div class="rm-drawer__section-title">全部菜单</div>
          <el-menu
            :default-active="$route.path"
            :collapse="false"
            router
            class="rm-drawer__menu"
            @select="showSidebarDrawer = false"
          >
            <template v-for="item in menuTree">
              <el-submenu v-if="item.children && item.children.length" :key="item.authId" :index="String(item.authId)">
                <template slot="title">
                  <div class="rm-drawer__menu-icon">
                    <i :class="item.authIcon || 'el-icon-folder'"></i>
                  </div>
                  <span>{{ item.authName }}</span>
                </template>
                <el-menu-item
                  v-for="child in item.children"
                  :key="child.authId"
                  :index="menuPath(child.authUrl)"
                >
                  <i :class="child.authIcon || 'el-icon-document'"></i>
                  <span>{{ child.authName }}</span>
                </el-menu-item>
              </el-submenu>
              <el-menu-item v-else :key="item.authId" :index="menuPath(item.authUrl)">
                <div class="rm-drawer__menu-icon">
                  <i :class="item.authIcon || 'el-icon-document'"></i>
                </div>
                <span>{{ item.authName }}</span>
              </el-menu-item>
            </template>
          </el-menu>
        </div>

        <!-- 底部用户区 -->
        <div class="rm-drawer__footer">
          <div class="rm-drawer__user">
            <div class="rm-drawer__avatar">
              <img v-if="userInfo.auHeader" :src="userInfo.auHeader" style="width:100%;height:100%;object-fit:cover;border-radius:10px;" />
              <span v-else>{{ avatarText }}</span>
            </div>
            <div class="rm-drawer__user-info">
              <span class="rm-drawer__username">{{ userName }}</span>
              <span class="rm-drawer__role">系统管理员</span>
            </div>
          </div>
          <button class="rm-drawer__logout" @click="handleLogout" title="退出登录">
            <i class="el-icon-switch-button"></i>
          </button>
        </div>
      </div>
    </el-drawer>
  </header>
</template>

<script>
import { updateRtUserPw, uploadFileToTxCloud } from '@/api/api'
import { updateRtUserHeader } from '@/api/system'
import { normalizeMenuPath } from '@/utils/menu'
export default {
  name: 'Navbar',
  props: {
    unreadCount: { type: Number, default: 0 }
  },
  data () {
    const validateConfirm = (rule, value, callback) => {
      if (value !== this.passwordForm.auPw) {
        callback(new Error('两次输入的密码不一致'))
      } else {
        callback()
      }
    }
    return {
      showSidebarDrawer: false,
      passwordDialogVisible: false,
      passwordLoading: false,
      profileDialogVisible: false,
      avatarUploading: false,
      // 菜单搜索状态
      searchVisible: false,
      searchKeyword: '',
      searchActiveIndex: 0,
      passwordForm: { auPwOld: '', auPw: '', auPwConfirm: '' },
      passwordRules: {
        auPwOld: [{ required: true, message: '请输入原密码', trigger: 'blur' }],
        auPw: [{ required: true, message: '请输入新密码', trigger: 'blur' }, { min: 6, message: '密码至少6位', trigger: 'blur' }],
        auPwConfirm: [{ required: true, message: '请再次输入新密码', trigger: 'blur' }, { validator: validateConfirm, trigger: 'blur' }]
      }
    }
  },
  computed: {
    userInfo () {
      return this.$store.state.user.userInfo || {}
    },
    userName () {
      return this.userInfo.auName || '用户'
    },
    // 是否管理员视图（auPost=1 为系统管理员）：管理员隐藏消息通知/意见反馈图标
    isAdminView () {
      const post = Number(this.userInfo.auPost || this.userInfo.post || this.userInfo.userPost || 0)
      return post === 1
    },
    avatarText () {
      return this.userName.slice(0, 1)
    },
    menuTree () {
      return this.$store.state.permission.menuTree || []
    },
    maxVisibleItems () {
      const width = window.innerWidth
      if (width >= 1600) return 8
      if (width >= 1400) return 7
      if (width >= 1200) return 6
      if (width >= 992) return 5
      return 3
    },
    visibleMenuItems () {
      return this.menuTree.slice(0, this.maxVisibleItems)
    },
    hiddenMenuItems () {
      return this.menuTree.slice(this.maxVisibleItems)
    },
    // 拍平当前账号权限下所有可点击菜单（带所属分组名，用于搜索）
    flatMenuList () {
      const result = []
      const walk = (nodes, groupName = '') => {
        ;(nodes || []).forEach(node => {
          if (!node) return
          // 跳过首页本身，避免搜索出看板
          const path = normalizeMenuPath(node.authUrl)
          if (path && path !== '/dashboard') {
            result.push({ ...node, _groupName: groupName || node.authName || '' })
          }
          if (node.children && node.children.length) {
            walk(node.children, groupName || node.authName)
          }
        })
      }
      walk(this.menuTree)
      return result
    },
    // 搜索过滤结果（按名称包含关键词，最多返回 8 条）
    searchResults () {
      const kw = String(this.searchKeyword || '').trim().toLowerCase()
      const list = this.flatMenuList
      if (!kw) return list.slice(0, 8)
      return list.filter(item => {
        const name = String(item.authName || '').toLowerCase()
        return name.includes(kw)
      }).slice(0, 8)
    }
  },
  watch: {
    // 关键词变化或结果集变化时，重置选中项到第一项
    searchResults () {
      this.searchActiveIndex = 0
    },
    searchKeyword () {
      this.searchActiveIndex = 0
    }
  },
  mounted () {
    window.addEventListener('resize', this.handleResize)
    window.addEventListener('keydown', this.handleGlobalKeydown)
  },
  beforeDestroy () {
    window.removeEventListener('resize', this.handleResize)
    window.removeEventListener('keydown', this.handleGlobalKeydown)
  },
  methods: {
    menuPath (path) {
      return normalizeMenuPath(path)
    },
    handleResize () {
      this.$forceUpdate()
    },
    handleMenuClick (path) {
      const targetPath = normalizeMenuPath(path)
      if (targetPath && targetPath !== this.$route.path) {
        this.$router.push(targetPath).catch(() => {})
      }
    },
    isMenuActive (item) {
      const itemPath = normalizeMenuPath(item.authUrl)
      if (this.$route.path === itemPath) return true
      if (item.children) {
        return item.children.some(child => this.$route.path === normalizeMenuPath(child.authUrl))
      }
      return false
    },

    // ============ 菜单搜索（Ctrl/Cmd+K 唤起，键盘上下导航，回车跳转） ============
    toggleSearch () {
      this.searchVisible = !this.searchVisible
      if (this.searchVisible) {
        this.searchActiveIndex = 0
        this.$nextTick(() => this.focusSearchInput())
      }
    },
    closeSearch () {
      this.searchVisible = false
      this.searchKeyword = ''
      this.searchActiveIndex = 0
    },
    focusSearchInput () {
      const comp = this.$refs.searchInput
      if (comp) {
        // el-input 组件实例：通过 focus() 聚焦内部 input
        const inner = comp.$el && comp.$el.querySelector ? comp.$el.querySelector('input') : null
        if (inner && typeof inner.focus === 'function') {
          inner.focus()
          if (typeof inner.select === 'function') inner.select()
        } else if (typeof comp.focus === 'function') {
          comp.focus()
        }
      }
    },
    // 上下移动选中项
    moveSearchActive (dir) {
      const len = this.searchResults.length
      if (!len) return
      let idx = this.searchActiveIndex + dir
      if (idx < 0) idx = len - 1
      if (idx >= len) idx = 0
      this.searchActiveIndex = idx
    },
    // 回车跳转当前选中项
    enterSearch () {
      const item = this.searchResults[this.searchActiveIndex]
      if (item) this.handleSearchSelect(item)
    },
    // 选中并跳转
    handleSearchSelect (item) {
      const targetPath = normalizeMenuPath(item && item.authUrl)
      if (!targetPath) return
      this.closeSearch()
      if (this.$route.path === targetPath) return
      this.$router.push(targetPath).catch(() => {})
    },
    // 关键词高亮（XSS 安全：先转义文本，再包裹高亮标签）
    highlightText (text) {
      const safe = String(text == null ? '' : text)
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
      const kw = String(this.searchKeyword || '').trim()
      if (!kw) return safe
      const safeKw = kw.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
      return safe.replace(new RegExp(safeKw, 'gi'), m => `<em class="rm-search-hl">${m}</em>`)
    },
    // 按菜单所属分组名判定 icon 配色（与首页分组色系一致）
    // 返回 rm-tone- 命名空间类名，避免与全局 is-* 类名冲突
    resolveSearchIconTone (item) {
      const group = item && (item._groupName || item.parentName || '')
      const g = String(group || '')
      if (/(用户|权限|账号|部门|角色|组织|人员|成员|中心)/.test(g)) return 'rm-tone-blue'
      if (/(报告|模板|审核|纷享|申请|行程)/.test(g)) return 'rm-tone-pink'
      if (/(系统|日志|通知|异常|统计|反馈|菜单|配置)/.test(g)) return 'rm-tone-green'
      return 'rm-tone-neutral'
    },
    // 全局快捷键：Ctrl/Cmd + K 唤起搜索
    handleGlobalKeydown (e) {
      if ((e.ctrlKey || e.metaKey) && (e.key === 'k' || e.key === 'K')) {
        e.preventDefault()
        this.toggleSearch()
      }
    },
    handleAccountCommand (command) {
      if (command === 'logout') {
        this.handleLogout()
        return
      }
      if (command === 'profile') {
        this.profileDialogVisible = true
        return
      }
      if (command === 'password') {
        this.passwordForm = { auPwOld: '', auPw: '', auPwConfirm: '' }
        this.passwordDialogVisible = true
        this.$nextTick(() => this.$refs.passwordForm?.clearValidate())
        return
      }
    },
    triggerAvatarUpload () {
      this.$refs.avatarInput?.click()
    },
    async handleAvatarChange (e) {
      const file = e.target.files?.[0]
      if (!file) return
      if (file.size > 5 * 1024 * 1024) {
        this.$message.warning('图片大小不能超过 5MB')
        return
      }
      this.avatarUploading = true
      try {
        const uploadRes = await uploadFileToTxCloud(file)
        const headerUrl = uploadRes.data || uploadRes
        await updateRtUserHeader({ auHeader: typeof headerUrl === 'string' ? headerUrl : headerUrl.url || '' })
        await this.$store.dispatch('user/refreshUserInfo')
        this.$message.success('头像更新成功')
      } catch (err) {
        this.$message.error(err.message || '头像上传失败')
      } finally {
        this.avatarUploading = false
        if (this.$refs.avatarInput) this.$refs.avatarInput.value = ''
      }
    },
    openPasswordFromProfile () {
      this.profileDialogVisible = false
      this.passwordForm = { auPwOld: '', auPw: '', auPwConfirm: '' }
      this.passwordDialogVisible = true
      this.$nextTick(() => this.$refs.passwordForm?.clearValidate())
    },
    async handlePasswordSubmit () {
      try {
        await this.$refs.passwordForm.validate()
      } catch { return }
      this.passwordLoading = true
      try {
        await updateRtUserPw({ auPw: this.passwordForm.auPw, auPwOld: this.passwordForm.auPwOld })
        this.$message.success('密码修改成功，请重新登录')
        this.passwordDialogVisible = false
        this.handleLogout()
      } catch (e) {
        this.$message.error(e.message || '密码修改失败')
      } finally {
        this.passwordLoading = false
      }
    },
    async handleLogout () {
      await this.$confirm('确认退出当前账号吗？', '提示', { type: 'warning' })
      this.$store.dispatch('user/logout')
      this.$store.dispatch('permission/resetPermission')
      this.$router.push('/login')
    }
  }
}
</script>

<style lang="scss" scoped>
.rm-navbar {
  height: 56px;
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 100;
  display: flex;
  align-items: center;
  background: #fff;
  border-bottom: 1px solid #e2e8f0;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
  padding: 0 16px 0 12px;
}

// 全量菜单按钮
.rm-navbar__menu-toggle {
  width: 36px;
  height: 36px;
  border: none;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: transparent;
  color: #64748b;
  cursor: pointer;
  transition: all 0.2s;
  margin-right: 12px;

  i {
    font-size: 18px;
  }

  &:hover {
    background: #f1f5f9;
    color: #4f7cff;
  }
}

// Logo区域
.rm-navbar__logo {
  display: flex;
  align-items: center;
  gap: 10px;
  cursor: pointer;
  padding-right: 20px;
  border-right: 1px solid #e2e8f0;
  margin-right: 16px;
}

.rm-navbar__logo-icon {
  width: 32px;
  height: 32px;
  background: linear-gradient(135deg, #4f7cff 0%, #7c3aed 100%);
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #fff;

  svg {
    width: 18px;
    height: 18px;
  }
}

.rm-navbar__logo-text {
  font-size: 16px;
  font-weight: 600;
  color: #1f2937;
  white-space: nowrap;
}

// 顶部菜单
.rm-navbar__menu {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 4px;
  overflow: hidden;
}

.rm-navbar__menu-item {
  position: relative;
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 6px 14px;
  border-radius: 10px;
  font-size: 14px;
  color: #64748b;
  cursor: pointer;
  transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1);
  white-space: nowrap;
  background: rgba(255, 255, 255, 0.6);
  border: 1px solid transparent;
  backdrop-filter: blur(4px);
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.02);

  &:hover {
    background: rgba(79, 124, 255, 0.08);
    color: #4f7cff;
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(79, 124, 255, 0.12);
    border-color: rgba(79, 124, 255, 0.15);

    i {
      color: #4f7cff;
    }
  }

  &.is-active {
    background: linear-gradient(135deg, rgba(79, 124, 255, 0.14) 0%, rgba(124, 58, 237, 0.08) 100%);
    color: #4f7cff;
    font-weight: 600;
    border: 1px solid rgba(79, 124, 255, 0.25);
    box-shadow: 0 4px 14px rgba(79, 124, 255, 0.18), inset 0 1px 0 rgba(255, 255, 255, 0.4);
    transform: translateY(-1px);

    i {
      color: #4f7cff;
    }

    &::before {
      content: '';
      position: absolute;
      bottom: 4px;
      left: 50%;
      transform: translateX(-50%);
      width: 16px;
      height: 3px;
      background: linear-gradient(90deg, #4f7cff 0%, #7c3aed 100%);
      border-radius: 2px;
      opacity: 0.8;
    }
  }

  i {
    font-size: 16px;
    color: #94a3b8;
    transition: color 0.25s ease;
  }
}

.rm-navbar__menu-arrow {
  font-size: 11px !important;
  margin-left: 2px;
  opacity: 0.7;
}

.rm-navbar__menu-more {
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  color: #64748b;

  &:hover {
    background: #f1f5f9;
    border-color: #cbd5e1;
    color: #475569;
  }
}

// 右侧工具区
.rm-navbar__right {
  display: flex;
  align-items: center;
  gap: 8px;
  padding-left: 20px;
  border-left: 1px solid #e5e7eb;

  // 修复：el-popover 的 slot=reference 会在按钮外层插入 <span> 包裹层，
  // 该包裹层默认 inline 不参与 flex 对齐，会撑高并破坏后续用户区的水平布局。
  // 让该包裹层也成为 flex 子项，保持右侧工具区水平居中对齐。
  ::v-deep > .el-popover__reference-wrapper {
    display: inline-flex;
    align-items: center;
  }
}

.rm-navbar__tool {
  width: 36px;
  height: 36px;
  border: none;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: transparent;
  color: #6b7280;
  cursor: pointer;
  transition: all 0.2s;

  &:hover,
  &.is-on {
    background: #f3f4f6;
    color: #4f7cff;
  }

  i {
    font-size: 18px;
  }
}

// 用户信息
.rm-navbar__user {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 6px 12px 6px 8px;
  border-radius: 20px;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background: #f3f4f6;
  }
}

.rm-navbar__avatar {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #4f7cff 0%, #7c3aed 100%);
  color: #fff;
  font-weight: 600;
  font-size: 14px;
  overflow: hidden;
  flex-shrink: 0;
}

.rm-navbar__avatar-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.rm-navbar__username {
  font-size: 14px;
  font-weight: 500;
  color: #374151;
  max-width: 80px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
</style>

<style lang="scss">
.rm-password-dialog {
  border-radius: 12px;
  .el-dialog__header { border-bottom: 1px solid #eef1f6; padding-bottom: 14px; }
  .el-dialog__title { font-weight: 600; font-size: 15px; }
}

.rm-profile-dialog {
  border-radius: 14px;
  .el-dialog__header { border-bottom: 1px solid #eef1f6; padding-bottom: 14px; }
  .el-dialog__title { font-weight: 600; font-size: 15px; }
  .el-dialog__body { padding: 24px 28px; }
}

.rm-profile {
  display: flex;
  flex-direction: column;
  align-items: center;
}

.rm-profile__avatar-section {
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-bottom: 24px;
}

.rm-profile__avatar {
  position: relative;
  width: 88px;
  height: 88px;
  border-radius: 50%;
  overflow: hidden;
  cursor: pointer;
  background: linear-gradient(135deg, #4f7cff 0%, #7c3aed 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 4px 16px rgba(79, 124, 255, 0.25);
  transition: box-shadow 0.2s;

  &:hover {
    box-shadow: 0 6px 24px rgba(79, 124, 255, 0.35);
    .rm-profile__avatar-overlay { opacity: 1; }
  }
}

.rm-profile__avatar-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.rm-profile__avatar-text {
  color: #fff;
  font-weight: 600;
  font-size: 32px;
}

.rm-profile__avatar-overlay {
  position: absolute;
  inset: 0;
  background: rgba(0, 0, 0, 0.4);
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0;
  transition: opacity 0.2s;

  i { color: #fff; font-size: 22px; }
}

.rm-profile__avatar-hint {
  margin-top: 8px;
  font-size: 12px;
  color: #94a3b8;
}

.rm-profile__info {
  width: 100%;
  border-top: 1px solid #f1f5f9;
  border-bottom: 1px solid #f1f5f9;
  padding: 16px 0;
  margin-bottom: 20px;
}

.rm-profile__item {
  display: flex;
  align-items: center;
  padding: 8px 4px;

  label {
    width: 60px;
    flex-shrink: 0;
    font-size: 13px;
    color: #94a3b8;
  }
  span {
    font-size: 14px;
    color: #334155;
  }
}

.rm-profile__pwd-btn {
  width: 100%;
}

// 下拉菜单样式
.rm-navbar__submenu {
  margin-top: 4px;
  padding: 8px;
  border-radius: 12px;
  border: 1px solid #e2e8f0;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.12);
  max-height: 280px;
  overflow-y: auto;
  background: #fff;

  &::-webkit-scrollbar {
    width: 4px;
  }
  &::-webkit-scrollbar-track {
    background: transparent;
  }
  &::-webkit-scrollbar-thumb {
    background: #cbd5e1;
    border-radius: 2px;
  }

  .el-dropdown-menu__item {
    position: relative;
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 10px 14px;
    border-radius: 10px;
    font-size: 14px;
    color: #475569;
    margin: 3px 0;
    transition: all 0.2s ease;
    overflow: hidden;

    i {
      font-size: 16px;
      color: #94a3b8;
      transition: color 0.2s ease;
    }

    &:hover {
      background: rgba(79, 124, 255, 0.06);
      color: #4f7cff;
      transform: translateX(2px);

      i {
        color: #4f7cff;
      }
    }

    &.is-active {
      background: linear-gradient(90deg, rgba(79, 124, 255, 0.1) 0%, rgba(124, 58, 237, 0.05) 100%);
      color: #4f7cff;
      font-weight: 600;
      border-left: 3px solid #4f7cff;

      i {
        color: #4f7cff;
      }
    }
  }

  .rm-navbar__submenu-group {
    font-weight: 600;
    color: #1e293b !important;
    background: #f1f5f9 !important;
    margin: 6px 0 4px;
    cursor: default;
    border-radius: 6px;

    &:first-child {
      margin-top: 0;
    }
  }
}

// 左侧抽屉样式 - 浅色现代化主题
.rm-sidebar-drawer {
  &.el-drawer__wrapper {
    .el-drawer {
      z-index: 2001 !important;
    }
  }

  .v-modal {
    z-index: 2000 !important;
  }

  .el-drawer__body {
    padding: 0;
    overflow: hidden;
  }

  .rm-drawer {
    height: 100%;
    display: flex;
    flex-direction: column;
    background: #fff;
  }

  .rm-drawer__brand {
    position: relative;
    padding: 24px 20px 20px;
    background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
    border-bottom: 1px solid #e2e8f0;
  }

  .rm-drawer__brand-content {
    display: flex;
    align-items: center;
    gap: 14px;
  }

  .rm-drawer__logo {
    width: 44px;
    height: 44px;
    background: linear-gradient(135deg, #4f7cff 0%, #7c3aed 100%);
    border-radius: 12px;
    display: flex;
    align-items: center;
    justify-content: center;
    color: #fff;
    box-shadow: 0 4px 14px rgba(79, 124, 255, 0.3);

    svg {
      width: 24px;
      height: 24px;
    }
  }

  .rm-drawer__brand-text {
    display: flex;
    flex-direction: column;
    gap: 2px;
  }

  .rm-drawer__brand-title {
    font-size: 17px;
    font-weight: 700;
    color: #1e293b;
    letter-spacing: 0.3px;
  }

  .rm-drawer__brand-subtitle {
    font-size: 11px;
    color: #64748b;
    letter-spacing: 1px;
    text-transform: uppercase;
  }

  .rm-drawer__close {
    position: absolute;
    top: 20px;
    right: 16px;
    width: 32px;
    height: 32px;
    border: none;
    border-radius: 8px;
    background: transparent;
    color: #94a3b8;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: all 0.2s;

    &:hover {
      background: #f1f5f9;
      color: #475569;
    }
  }

  .rm-drawer__section-title {
    font-size: 11px;
    font-weight: 600;
    color: #94a3b8;
    text-transform: uppercase;
    letter-spacing: 1px;
    padding: 0 20px;
    margin-bottom: 12px;
  }

  .rm-drawer__menu-wrapper {
    flex: 1;
    overflow-y: auto;
    padding: 20px 0;

    &::-webkit-scrollbar {
      width: 4px;
    }
    &::-webkit-scrollbar-track {
      background: transparent;
    }
    &::-webkit-scrollbar-thumb {
      background: #e2e8f0;
      border-radius: 2px;
    }
  }

  .rm-drawer__menu {
    border-right: none;
    background: transparent;
    padding: 0 16px;

    .el-menu-item,
    .el-submenu__title {
      height: 44px;
      line-height: 44px;
      margin: 4px 0;
      padding: 0 12px !important;
      border-radius: 10px;
      color: #475569 !important;
      font-size: 14px;
      display: flex;
      align-items: center;
      gap: 10px;
      transition: all 0.2s;

      &:hover {
        background: #f8fafc !important;
        color: #1e293b !important;
      }
    }

    .el-menu-item.is-active {
      background: linear-gradient(90deg, rgba(79, 124, 255, 0.12) 0%, rgba(124, 58, 237, 0.06) 100%) !important;
      color: #4f7cff !important;
      font-weight: 600;
      border: 1px solid rgba(79, 124, 255, 0.25);
      box-shadow: 0 4px 12px rgba(79, 124, 255, 0.1), inset 0 1px 0 rgba(255, 255, 255, 0.4);

      i {
        color: #4f7cff !important;
      }
    }

    .el-submenu {
      .el-menu-item {
        padding-left: 46px !important;
        height: 38px;
        line-height: 38px;
        font-size: 13px;
        color: #64748b !important;

        &.is-active {
          background: linear-gradient(90deg, rgba(79, 124, 255, 0.1) 0%, rgba(124, 58, 237, 0.04) 100%) !important;
          color: #4f7cff !important;
          font-weight: 600;
          border-left: 3px solid #4f7cff;
          box-shadow: 0 2px 8px rgba(79, 124, 255, 0.08);

          i {
            color: #4f7cff !important;
          }
        }
      }

      .el-submenu__icon-arrow {
        color: #94a3b8;
      }
    }
  }

  .rm-drawer__menu-icon {
    width: 28px;
    height: 28px;
    border-radius: 8px;
    background: #f1f5f9;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: all 0.2s;

    i {
      font-size: 14px;
      color: #64748b;
    }
  }

  .el-menu-item:hover .rm-drawer__menu-icon,
  .el-submenu__title:hover .rm-drawer__menu-icon {
    background: #e2e8f0;
  }

  .el-menu-item.is-active .rm-drawer__menu-icon {
    background: rgba(79, 124, 255, 0.12);

    i {
      color: #4f7cff !important;
    }
  }

  .rm-drawer__footer {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 16px 20px;
    background: #f8fafc;
    border-top: 1px solid #f1f5f9;
  }

  .rm-drawer__user {
    display: flex;
    align-items: center;
    gap: 12px;
  }

  .rm-drawer__avatar {
    width: 40px;
    height: 40px;
    border-radius: 10px;
    background: linear-gradient(135deg, #4f7cff 0%, #7c3aed 100%);
    display: flex;
    align-items: center;
    justify-content: center;
    color: #fff;
    font-weight: 600;
    font-size: 15px;
    box-shadow: 0 2px 8px rgba(79, 124, 255, 0.25);
  }

  .rm-drawer__user-info {
    display: flex;
    flex-direction: column;
    gap: 2px;
  }

  .rm-drawer__username {
    font-size: 14px;
    font-weight: 600;
    color: #1e293b;
  }

  .rm-drawer__role {
    font-size: 12px;
    color: #64748b;
  }

  .rm-drawer__logout {
    width: 36px;
    height: 36px;
    border: none;
    border-radius: 10px;
    background: #fef2f2;
    color: #ef4444;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: all 0.2s;

    i {
      font-size: 18px;
    }

    &:hover {
      background: #fecaca;
      transform: scale(1.05);
    }
  }
}

// ============================================================
// 菜单搜索弹出面板（append-to-body，需非 scoped 全局样式）
// 所有规则嵌套在 .rm-navbar-search-popper 内，提高特异性避免被全局样式覆盖
// 配色与项目主题一致：主色 #4f7cff，分组色系蓝/粉/绿/中性
// ============================================================
.rm-navbar-search-popper {
  padding: 0 !important;
  border: 1px solid #e2e8f0 !important;
  border-radius: 14px !important;
  box-shadow: 0 20px 48px rgba(15, 23, 42, 0.18), 0 4px 12px rgba(15, 23, 42, 0.06) !important;
  overflow: hidden;
  background: #fff;

  // 面板根容器
  .rm-sp {
    display: flex;
    flex-direction: column;
    max-height: 460px;
    width: 100%;
  }

  // ===== 顶部输入区 =====
  .rm-sp__head {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 12px 14px;
    border-bottom: 1px solid #f1f5f9;

    .rm-sp__head-icon {
      color: #94a3b8;
      font-size: 16px;
      flex-shrink: 0;
    }

    // 让 el-input 撑满、无边框融入面板
    .rm-sp__head-input {
      flex: 1;
      min-width: 0;

      .el-input__inner {
        border: none !important;
        background: transparent !important;
        height: 32px !important;
        line-height: 32px !important;
        padding: 0 !important;
        font-size: 14px !important;
        color: #0f172a !important;
        box-shadow: none !important;

        &::placeholder {
          color: #cbd5e1 !important;
        }
      }
    }

    .rm-sp__head-esc {
      flex-shrink: 0;
      height: 20px;
      padding: 0 6px;
      border-radius: 5px;
      font-size: 11px;
      font-family: inherit;
      line-height: 20px;
      color: #94a3b8;
      background: #f1f5f9;
      border: 1px solid #e2e8f0;
    }
  }

  // ===== 结果列表区 =====
  .rm-sp__body {
    flex: 1;
    overflow-y: auto;
    padding: 8px;

    &::-webkit-scrollbar {
      width: 5px;
    }
    &::-webkit-scrollbar-thumb {
      background: #e2e8f0;
      border-radius: 3px;
    }
  }

  // ===== 单条结果 =====
  .rm-sp__row {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 9px 12px;
    border-radius: 10px;
    cursor: pointer;
    transition: background 0.15s ease;

    &:hover {
      background: #f8fafc;
    }

    &.rm-sp__row--on {
      background: linear-gradient(90deg, rgba(79, 124, 255, 0.08) 0%, rgba(124, 58, 237, 0.04) 100%);
      box-shadow: inset 3px 0 0 #4f7cff;
    }
  }

  .rm-sp__icon {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 34px;
    height: 34px;
    border-radius: 9px;
    color: #fff;
    font-size: 16px;
    flex-shrink: 0;

    &.rm-tone-blue { background: linear-gradient(135deg, #4f7cff, #6b9cff); }
    &.rm-tone-pink { background: linear-gradient(135deg, #a855f7, #ec4899); }
    &.rm-tone-green { background: linear-gradient(135deg, #10b981, #34d399); }
    &.rm-tone-neutral { background: linear-gradient(135deg, #64748b, #94a3b8); }
  }

  .rm-sp__text {
    display: flex;
    flex: 1;
    min-width: 0;
    flex-direction: column;
    gap: 2px;
    overflow: hidden;
  }

  .rm-sp__name {
    font-size: 14px;
    font-weight: 600;
    color: #1e293b;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .rm-sp__path {
    font-size: 12px;
    color: #94a3b8;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .rm-sp__enter {
    flex-shrink: 0;
    font-size: 12px;
    color: #cbd5e1;
    font-style: normal;
  }

  // 关键词高亮
  .rm-search-hl {
    color: #4f7cff;
    background: rgba(79, 124, 255, 0.12);
    border-radius: 3px;
    padding: 0 2px;
    font-style: normal;
  }

  // ===== 空态 =====
  .rm-sp__empty {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 8px;
    padding: 36px 20px;
    color: #cbd5e1;

    i {
      font-size: 28px;
    }

    p {
      margin: 0;
      font-size: 13px;
      color: #94a3b8;
    }
  }

  // ===== 底部快捷键提示 =====
  .rm-sp__foot {
    display: flex;
    align-items: center;
    gap: 16px;
    padding: 10px 14px;
    border-top: 1px solid #f1f5f9;
    background: #fafbfc;

    span {
      display: inline-flex;
      align-items: center;
      gap: 4px;
      font-size: 12px;
      color: #94a3b8;
    }

    kbd {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      min-width: 18px;
      height: 18px;
      padding: 0 4px;
      border-radius: 4px;
      font-size: 11px;
      font-family: inherit;
      color: #64748b;
      background: #fff;
      border: 1px solid #e2e8f0;
      box-shadow: 0 1px 0 #e2e8f0;
    }
  }
}
</style>
